/**
 * Run a callback when the browser tab returns to the foreground.
 *
 * Use case (PR 6d): the user clicks "Manage subscription", lands in
 * Stripe Billing Portal in a new tab, changes plan, closes the tab.
 * When they return to StepFlow, ``OrgCreditRing`` and
 * ``BillingBudgetBanner`` should reflect the new plan immediately —
 * not on the next route change or a manual refresh. The Portal's own
 * ``return_url`` redirect happens INSIDE its own tab, so we can't rely
 * on a navigation event in our app.
 *
 * Why ``visibilitychange`` and not ``focus``:
 *   * ``focus`` fires on every input click within the page — way too
 *     spammy for a "refetch entitlement" callback.
 *   * ``visibilitychange`` fires only on tab switches. Cheap to listen,
 *     fires exactly when we want.
 *
 * The callback is debounced to ``hidden → visible`` transitions only
 * (filtered via ``document.visibilityState``). Initial mount is NOT
 * a refetch trigger — callers handle their own initial fetch in their
 * own ``useEffect``.
 *
 * @param {() => void} callback - invoked each time the tab becomes
 *   visible. Should be stable (use ``useCallback`` upstream) — this
 *   hook re-subscribes when the callback identity changes.
 * @param {boolean} [enabled=true] - opt-out. When false, no listener
 *   is registered. Use this to gate by RBAC (don't subscribe a non-
 *   payer user to billing refetches).
 */
import { useEffect } from 'react';

export function useTabFocusRefetch(callback, enabled = true) {
  useEffect(() => {
    if (!enabled || typeof callback !== 'function') return undefined;
    if (typeof document === 'undefined') return undefined;

    const onVis = () => {
      if (document.visibilityState === 'visible') {
        callback();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [callback, enabled]);
}

export default useTabFocusRefetch;
